import React from 'react';
import styled from 'styled-components';

import { Colors, BREAK_POINTS } from '@origin-digital/style-guide';
import { CheckIcon } from '@origin-digital/icon';

import { WizardContext } from '../WizardContext';
import { IStepIndicatorProps, IStepIndicatorStyleProps } from './StepIndicator.types';

const Indicator = styled.div<IStepIndicatorStyleProps>`
  position: relative;
  width: 32px;
  text-align: center;
  color: ${Colors.GREY80};
  cursor: ${(props) => (props.complete && !props.disabled ? 'pointer' : 'default')};

  @media screen and (min-width: ${BREAK_POINTS.Min.SM}) {
    width: 48px;
  }
  @media screen and (min-width: ${BREAK_POINTS.Min.MD}) {
    width: 64px;
  }

  &:not(:first-child)::before {
    content: '';
    position: absolute;
    top: 50%;
    height: 2px;
    margin-top: -1px;
    left: 0;
    background-color: ${(props) =>
      props.active || props.complete ? Colors.LIGHT_ORANGE : Colors.GREY16};
    width: calc(50% - 14px);

    @media screen and (min-width: ${BREAK_POINTS.Min.MD}) {
      width: calc(50% - 20px);
    }
  }

  &:not(:last-child)::after {
    content: '';
    position: absolute;
    top: 50%;
    margin-top: -1px;
    height: 2px;
    right: 0;
    background-color: ${(props) => (props.complete ? Colors.LIGHT_ORANGE : Colors.GREY16)};
    width: calc(50% - 14px);

    @media screen and (min-width: ${BREAK_POINTS.Min.MD}) {
      width: calc(50% - 20px);
    }
  }
`;

const Step = styled.div<IStepIndicatorStyleProps>`
  width: 14px;
  height: 14px;
  border-radius: 50%;
  text-align: center;
  background-color: ${(props) =>
    props.active || props.complete ? Colors.LIGHT_ORANGE : Colors.GREY16};
  margin: 0 auto;
  font-size: 10px;
  position: relative;
  
  ${(props) =>
    props.complete &&
    `&:hover {
    opacity: 0.5;
  }`}
  
  @media screen and (min-width: ${BREAK_POINTS.Min.SM}) {
    width: 24px;
    height: 24px;
    line-height: 24px;
  }

  & > * {
    display: none;

    @media screen and (min-width: ${BREAK_POINTS.Min.SM}) {
      display: inline;
    }
  }

  svg {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
  }
`;

class StepIndicator extends React.Component<IStepIndicatorProps> {
  static contextType = WizardContext;

  isDisabled() {
    const { currentStep, step } = this.props;
    const { answers, config } = this.context;
    const stepConfig = config.steps[step - 1];

    return !!(currentStep >= step && stepConfig.disabled && stepConfig.disabled(answers));
  }

  public indicatorProps() {
    const { currentStep, step } = this.props;
    const isDisabled = this.isDisabled();
    return {
      active: currentStep === step,
      complete: currentStep > step,
      disabled: isDisabled,
    };
  }

  handleClick = () => {
    const { step, currentStep, goToStep } = this.props;

    // Only allow navigating to a previously completed step
    if (currentStep > step && !this.isDisabled()) {
      goToStep(step);
    }
  };

  render() {
    const { currentStep, step } = this.props;
    const indicatorProps = this.indicatorProps();

    if (this.isDisabled()) {
      return null;
    }

    return (
      <Indicator {...indicatorProps} onClick={this.handleClick}>
        <Step {...indicatorProps}>
          {currentStep > step ? <CheckIcon size={14} color="grey80" /> : <span>{step}</span>}
        </Step>
      </Indicator>
    );
  }
}

export { StepIndicator, Step };
